import notEmpty from "@/functions/notEmpty";
import {
  IssueOrder,
  PreventArchivingType,
  SQDCActionStatus,
  SQDCBoardEventStatus,
  SQDCBoardIssueConnectionTypes,
  TaskStatus,
  TaskTyp,
  type ActionListActionFields_Task_Fragment,
  type EABEventDataFragment,
  type EventListEventFields_SQDCEventOldFormatNewtuff_Fragment,
  type EventListEventFieldsFragment,
} from "@/graphql";
import { computed, type ComputedRef } from "vue";

function convertBoardStatusToOldFormat(status: SQDCBoardEventStatus): number {
  switch (status) {
    case SQDCBoardEventStatus.CREATED:
      return SQDCBoardIssueConnectionTypes.created;
    case SQDCBoardEventStatus.DELEGATED:
      return SQDCBoardIssueConnectionTypes.delegate;
    case SQDCBoardEventStatus.ESCALATED:
      return SQDCBoardIssueConnectionTypes.escalate;
  }
}

function convertActionStatusToOldFormat(status: SQDCActionStatus): number {
  switch (status) {
    case SQDCActionStatus.PLAN:
      return TaskStatus.plan;
    case SQDCActionStatus.DO:
      return TaskStatus.do;
    case SQDCActionStatus.CHECK:
      return TaskStatus.check;
    case SQDCActionStatus.ACT:
      return TaskStatus.act;
  }
}

export function useEventTranslationLayer(
  oldEvents: ComputedRef<EventListEventFieldsFragment[] | undefined>,
  newEvents: ComputedRef<EABEventDataFragment[] | undefined>,
  eventOrder: ComputedRef<IssueOrder | undefined>,
) {
  const events = computed<EventListEventFieldsFragment[]>(() =>
    [
      ...(oldEvents.value ?? []),
      ...(newEvents.value?.map(
        (event): EventListEventFields_SQDCEventOldFormatNewtuff_Fragment => ({
          __typename: "SQDCEventOldFormatNewtuff",
          id: event.id,
          createdAt: event.createdAt,
          eisenhowerPriority: event.eisenhowerPriority,
          forwards: [],
          forwardsAsTarget: [],
          linkedEvents: [],
          occurrenceDate: event.occurrenceDate,
          updatedAt: event.updatedAt,
          deletedAt: event.deletedAt,
          description: event.SQDCEventTextFields.map((field) => field.value).join(" "),
          owner:
            event.Creator == null
              ? undefined
              : {
                  id: event.Creator.id,
                  preferredName: event.Creator.preferredName,
                },
          preventsArchiving: event.ActionEventConnection.some(
            (conn) => conn.action?.status !== SQDCActionStatus.ACT,
          )
            ? PreventArchivingType.ACTION_STATUS
            : undefined,
          boardConnection: event.BoardEventConnection.map((conn) => ({
            boardId: conn.boardId,
            status: convertBoardStatusToOldFormat(conn.status),
            reason: conn.reason,
            createdAt: conn.createdAt,
          })),
          flags: event.FlagEventConnection.map((flag) => ({
            id: flag.flagId,
            name: flag.flag.name,
            abbreviationId: flag.flag.abbreviationId,
            displayNameId: flag.flag.displayNameId,
            color: flag.flag.color,
          })),
          actions: event.ActionEventConnection.map(
            (conn): ActionListActionFields_Task_Fragment | null => {
              if (conn.action == null) {
                return null;
              }
              return {
                __typename: "Task",
                assigneeId: conn.action.assigneeId,
                createdAt: conn.action.createdAt,
                deadline: conn.action.deadline,
                description: conn.action.SQDCActionTextFields.map((field) => field.value).join(" "),
                id: conn.action.id,
                status: convertActionStatusToOldFormat(conn.action.status),
                preventsArchiving: conn.action.status !== SQDCActionStatus.ACT,
                type: TaskTyp.common,
                updatedAt: conn.action.updatedAt,
                assignee:
                  conn.action.Assignee == null
                    ? undefined
                    : {
                        id: conn.action.Assignee.id,
                        preferredName: conn.action.Assignee.preferredName,
                      },
                deletedAt: conn.action.deletedAt,
                drawFlowNode: undefined,
                drawFlowNodeId: undefined,
                Folder: undefined,
                folderId: undefined,
                meta: undefined,
                Revisions: [],
                TaskAuditResultConnection: undefined,
              };
            },
          ).filter(notEmpty),
          newFormat: event,
        }),
      ) ?? []),
    ].sort((a, b) => {
      if (eventOrder.value === IssueOrder.ASC) {
        return a.createdAt.localeCompare(b.createdAt);
      }
      return b.createdAt.localeCompare(a.createdAt);
    }),
  );

  return {
    events,
  };
}
